import { useEffect, useState } from 'react';
import { InitValues, SelectedOrgUnit } from './types';
import { useAuth } from 'src/state/auth';
import {
  FlatOrganisationsQuery,
  Role,
  useUserOrganisationLazyQuery,
  useUserWorkspaceLazyQuery,
} from 'src/graphql/_generated';

type UseInitialValues = (params: {
  selectedOrgUnit?: SelectedOrgUnit;
  userId: number;
  targetUserEmail: string | null;
  flatOrganisationsData: FlatOrganisationsQuery | undefined;
  isReady: boolean;
}) => {
  initError: string;
  initialValues: InitValues;
  isLoading: boolean;
};

const useInitialValues: UseInitialValues = ({
  selectedOrgUnit,
  userId,
  targetUserEmail,
  flatOrganisationsData,
  isReady,
}) => {
  const [initError, setInitError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<InitValues>({} as InitValues);
  const { profile } = useAuth();

  const [getUserWorkspace] = useUserWorkspaceLazyQuery();
  const [getUserOrganisation] = useUserOrganisationLazyQuery();

  useEffect(() => {
    setInitError('');
    setIsLoading(true);
    if (profile && isReady) {
      // GLOBAL SELF - use profile data
      if (!selectedOrgUnit) {
        setInitialValues({
          name: profile.user.name,
          profilePhotoPath: profile.user.profilePhotoPath,
          email: profile.user.email,
          defaultName: profile.user.name,
          receiveNotifications: !!profile.user.receiveNotifications,
        });
        setIsLoading(false);
      }

      // For workspace or organisation context, use selectedOrgUnit directly (it's already the full FlatOrganisation object)
      if (selectedOrgUnit) {
        if (!selectedOrgUnit.member?.association) {
          // Fallback: query the workspace/org directly to get full user data including receiveNotifications
          if (selectedOrgUnit.recordType === 'W') {
            getUserWorkspace({
              variables: { id: selectedOrgUnit.id, userId: userId.toString() },
              fetchPolicy: 'no-cache',
            })
              .then(({ data }) => {
                const usr = data?.workspace?.users[0];

                if (!usr) {
                  setInitError('User not found');
                  return;
                }

                setInitialValues({
                  name: usr.orgUnitIdentity?.name || usr.name,
                  profilePhotoPath: usr.profilePhotoPath,
                  email: usr.email,
                  defaultName: usr.name,
                  acceptedDate: usr.orgUnitIdentity?.acceptedDate,
                  receiveNotifications: !!usr.orgUnitIdentity?.receiveNotifications,
                  role: usr.orgUnitIdentity?.baseRole || Role.Collaborator,
                });
              })
              .catch((err) => setInitError(err?.message || 'Something went wrong'))
              .finally(() => setIsLoading(false));
          } else {
            getUserOrganisation({
              variables: { id: selectedOrgUnit.id, userId: userId.toString() },
              fetchPolicy: 'no-cache',
            })
              .then(({ data }) => {
                const usr = data?.organisation?.users[0];

                if (!usr) {
                  setInitError('User not found');
                  return;
                }

                setInitialValues({
                  name: usr.orgUnitIdentity?.name || usr.name,
                  profilePhotoPath: usr.profilePhotoPath,
                  email: usr.email,
                  defaultName: usr.name,
                  receiveNotifications: !!usr.orgUnitIdentity?.receiveNotifications,
                  role: usr.orgUnitIdentity?.baseRole || Role.Collaborator,
                });
              })
              .catch((err) => setInitError(err?.message || 'Something went wrong'))
              .finally(() => setIsLoading(false));
          }
        } else {
          // Use selectedOrgUnit.member.association directly (already available from flatOrganisations)
          const association = selectedOrgUnit.member.association;

          // For receiveNotifications, we need to query the workspace/org to get full user data
          // since it's not available in flatOrganisations member.association
          const fetchFullData = async () => {
            try {
              let receiveNotifications = false;

              if (selectedOrgUnit.recordType === 'W') {
                const { data } = await getUserWorkspace({
                  variables: { id: selectedOrgUnit.id, userId: userId.toString() },
                  fetchPolicy: 'no-cache',
                });
                receiveNotifications = !!data?.workspace?.users[0]?.orgUnitIdentity?.receiveNotifications;
              } else {
                const { data } = await getUserOrganisation({
                  variables: { id: selectedOrgUnit.id, userId: userId.toString() },
                  fetchPolicy: 'no-cache',
                });
                receiveNotifications = !!data?.organisation?.users[0]?.orgUnitIdentity?.receiveNotifications;
              }

              setInitialValues({
                name: association.userName || '',
                profilePhotoPath: null,
                email: association.userEmail || '',
                defaultName: association.userName || '',
                acceptedDate: association.acceptedDate,
                receiveNotifications,
                role: association.role || Role.Collaborator,
              });
            } catch (err: unknown) {
              const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
              setInitError(errorMessage);
            } finally {
              setIsLoading(false);
            }
          };

          fetchFullData();
        }
      }
    }
  }, [selectedOrgUnit, profile, isReady, flatOrganisationsData, targetUserEmail]);

  return { initError, initialValues, isLoading };
};

export default useInitialValues;
